import { apiClient } from './api';

export type TicketCategory =
  | 'customer' | 'delivery' | 'payment' | 'inventory'
  | 'compliance' | 'returns' | 'other';

export type TicketPriority = 'low' | 'medium' | 'high' | 'critical';

export type TicketStatus =
  | 'open' | 'in_progress' | 'waiting' | 'resolved' | 'closed'
  | 'pending_review' | 'approved' | 'rejected';

export type TicketSource =
  | 'customer_portal' | 'system_alert' | 'driver_app'
  | 'warehouse_staff' | 'call_center' | 'other';

export interface TicketMessage {
  id: number;
  ticket_id: number;
  sender_user_id: number | null;
  sender_name: string | null;
  sender_role: 'customer' | 'support' | 'system';
  body: string;
  is_internal: boolean;
  created_at: string;
}

export interface Ticket {
  id: number;
  ticket_number: string;
  category: TicketCategory;
  priority: TicketPriority;
  status: TicketStatus;
  subject: string;
  description: string | null;
  customer_id: number | null;
  customer_name: string | null;
  order_id: number | null;
  assigned_to_user_id: number | null;
  assigned_to_name: string | null;
  created_by_user_id: number;
  created_by_name: string | null;
  source: TicketSource;
  resolution_notes: string | null;
  sla_due_at: string | null;
  opened_at: string;
  resolved_at: string | null;
  closed_at: string | null;
  messages?: TicketMessage[];
  created_at: string;
  updated_at: string;
}

export interface TicketsSummary {
  total: number;
  open: number;
  in_progress: number;
  high_priority: number;
  overdue: number;
  resolved_today: number;
  by_category: Record<TicketCategory, number>;
}

export interface ListTicketsFilters {
  page?: number;
  limit?: number;
  category?: TicketCategory;
  priority?: TicketPriority;
  status?: TicketStatus;
  /** `'me'` is special-cased server-side to the authenticated caller's id. */
  assigned_to_user_id?: number | 'me';
  customer_id?: number;
  search?: string;
  open_only?: boolean;
  /** Quick filter: tickets with no assignee (VP-CS-001 "Unassigned"). */
  unassigned?: boolean;
  /** Quick filter: sla_due_at has passed and the ticket isn't resolved (VP-CS-001 "SLA"). */
  sla_breached?: boolean;
}

export interface CreateTicketInput {
  category: TicketCategory;
  priority?: TicketPriority;
  subject: string;
  description?: string;
  customer_id?: number;
  order_id?: number;
  assigned_to_user_id?: number;
  source?: TicketSource;
  sla_due_at?: string;
}

export interface UpdateTicketInput {
  category?: TicketCategory;
  priority?: TicketPriority;
  status?: TicketStatus;
  subject?: string;
  description?: string;
  assigned_to_user_id?: number | null;
  resolution_notes?: string;
  sla_due_at?: string | null;
}

export interface PaginatedTickets {
  data: Ticket[];
  pagination: { total: number; page: number; limit: number; pages: number };
}

export async function listTickets(filters: ListTicketsFilters = {}): Promise<PaginatedTickets> {
  return apiClient.get<PaginatedTickets>('/tickets', filters as Record<string, any>);
}

export async function getTicket(id: number | string): Promise<Ticket> {
  return apiClient.get<Ticket>(`/tickets/${id}`);
}

export async function createTicket(input: CreateTicketInput): Promise<Ticket> {
  return apiClient.post<Ticket>('/tickets', input);
}

export async function updateTicket(id: number | string, input: UpdateTicketInput): Promise<Ticket> {
  return apiClient.patch<Ticket>(`/tickets/${id}`, input);
}

export async function addTicketMessage(id: number | string, body: string, isInternal = false): Promise<TicketMessage> {
  return apiClient.post<TicketMessage>(`/tickets/${id}/messages`, { body, is_internal: isInternal });
}

export async function getTicketsSummary(): Promise<TicketsSummary> {
  return apiClient.get<TicketsSummary>('/tickets/summary');
}
